import { useEffect, useState } from 'react';
import {
  fetchBlogs,
  createBlog,
  updateBlog,
  deleteBlog,
} from '../../api/blogs';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { API_BASE_URL } from '../../utils/constants';

const quillFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'color',
  'background',
  'list',
  'bullet',
  'link',
  'align',
];

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};

const getToday = () => {
  const d = new Date();
  return d.toISOString().split("T")[0];
};

// Generate slug from title
const generateSlug = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const AdminBlogs = () => {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({
    title: '',
    date: getToday(),
    id: '',
    excerpt: '',
    content: '',
    image: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchBlogs();
        setPosts(data);
      } catch (err) {
        console.error('Error loading blogs:', err);
      }
    };
    load();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "title" && !form.id) {
      setForm((prev) => ({
        ...prev,
        title: value,
        id: generateSlug(value)
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleContentChange = (value) => {
    setForm((prev) => ({ ...prev, content: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] || null;
    setImageFile(file);
    setForm((prev) => ({ ...prev, image: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalForm = {
      ...form,
      id: form.id || generateSlug(form.title),
    };

    try {
      if (editingId) {
        const updated = await updateBlog(editingId, finalForm, imageFile);
        setPosts((prev) => prev.map((p) => (p.id === editingId ? updated : p)));
      } else {
        const created = await createBlog(finalForm, imageFile);
        setPosts((prev) => [created, ...prev]);
      }

      setEditingId(null);
      setImageFile(null);

      // Reset form
      setForm({
        title: '',
        date: getToday(),
        id: '',
        excerpt: '',
        content: '',
        image: '',
      });
    } catch (err) {
      console.error('Error submitting blog:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this post?')) {
      try {
        await deleteBlog(id);
        setPosts((prev) => prev.filter((p) => p.id !== id));
      } catch (err) {
        console.error('Error deleting blog:', err);
      }
    }
  };

  const handleEdit = (post) => {
    setForm({
      title: post.title,
      date: post.date || getToday(),
      id: post.slug || post.id || '',
      excerpt: post.excerpt || '',
      content: post.content,
      image: post.image || '',
    });
    setImageFile(null);
    setEditingId(post.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="text-xl font-bold"><a href='/admin/dashboard' className='hover:underline'>Admin</a> · Blogs</div>
      <h1 className="text-3xl font-bold mb-6 text-center">
        {editingId ? 'Edit Blog Post' : 'Add New Blog Post'}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-4 mb-10">
        <input
          name="title"
          value={form.title}
          onChange={handleChange}
          placeholder="Title"
          required
          className="w-full border p-2 rounded"
        />
        <input
          name="date"
          value={form.date}
          onChange={handleChange}
          placeholder="Date (e.g. 2025-06-15)"
          required
          className="w-full border p-2 rounded"
        />
        <input
          name="id"
          value={form.id}
          onChange={handleChange}
          placeholder="Id (e.g. my-post)"
          className="w-full border p-2 rounded"
        />
        <input
          name="excerpt"
          value={form.excerpt}
          onChange={handleChange}
          placeholder="Excerpt"
          className="w-full border p-2 rounded"
        />

        <div>
          <label className="block mb-2 font-semibold">Cover image</label>

          {form.image && !imageFile && (
            <div className="mb-3">
              <p className="text-xs text-gray-500 mb-1">Trenutna slika:</p>
              <img
                src={`${API_BASE_URL}/uploads/blogs/${form.image}`}
                alt={form.title}
                className="max-h-40 rounded-md border object-cover"
              />
            </div>
          )}

          {imageFile && (
            <div className="mb-3">
              <p className="text-xs text-gray-500 mb-1">Nova slika (preview):</p>
              <img
                src={URL.createObjectURL(imageFile)}
                alt="Nova slika"
                className="max-h-40 rounded-md border object-cover"
              />
            </div>
          )}

          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              setImageFile(file);
              setForm((prev) => ({ ...prev, image: file ? '' : prev.image }));
            }}
            className="w-full border p-2 rounded"
          />

          <p className="text-xs text-gray-500 mt-1">
            Ako izabereš novu sliku, stara će biti zamenjena i obrisana na serveru.
          </p>
        </div>

        <div>
          <h3 className="block mb-2 font-semibold">Content</h3>
          <ReactQuill
            theme="snow"
            value={form.content}
            onChange={handleContentChange}
            modules={quillModules}
            formats={quillFormats}
            className="bg-white [&_.ql-editor]:min-h-[250px] [&_.ql-container]:min-h-[300px]"
          />
        </div>

        <div className="flex gap-4">
          <button type="submit" className="bg-black text-white px-4 py-2 rounded">
            {editingId ? 'Update Post' : 'Add Blog Post'}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setForm({
                  title: '',
                  date: '',
                  id: '',
                  excerpt: '',
                  content: '',
                  image: '',
                });
                setImageFile(null);
              }}
              className="text-sm text-gray-500 underline"
            >
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      <div className="space-y-4">
        {posts.length === 0 && <p>No blog posts yet.</p>}
        {posts.map((post) => (
          <div key={post.id} className="border p-4 rounded">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="font-bold">{post.title}</h2>
                <p className="text-sm text-gray-500">{post.date}</p>
                <p className="text-sm mt-1">{post.excerpt}</p>
              </div>
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => handleEdit(post)}
                  className="text-blue-600 hover:underline text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(post.id)}
                  className="text-red-500 hover:underline text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminBlogs;
